import { isRuleList, type ReferenceRanges } from './referenceRangeRules';

/** Provenance of a measurement's ranges, as shipped in the v2 fixture. The
 *  label always renders; `file` names a PDF in the private guidelines folder
 *  (only hosted guidelines have one) — the backend signs a link for it on
 *  demand, so the fixture never carries a URL. */
export interface RangeSource {
  label: string;
  citation: string | null;
  file: string | null;
  /** Where in the source the band is stated, e.g. "Table 2, p.7". */
  location: string | null;
  /** PDF page index to open the hosted file at (1-based), when known. */
  page: number | null;
}

const PDF_FILE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]*\.pdf$/;

/**
 * The source behind a rule-list blob, or null when absent or malformed.
 * Falls back to the citation's first clause when the fixture has no label.
 */
export function getRangeSource(
  referenceRanges: ReferenceRanges | null | undefined
): RangeSource | null {
  if (!referenceRanges || typeof referenceRanges !== 'object') return null;
  if (!isRuleList(referenceRanges)) return null;
  const raw = referenceRanges.source;
  if (!raw || typeof raw !== 'object') return null;
  const { label, citation, file, location, page } = raw as Record<
    string,
    unknown
  >;
  const citationText = typeof citation === 'string' ? citation : null;
  const labelText =
    typeof label === 'string' && label.trim()
      ? label.trim()
      : (citationText?.split(/:\s|\. /, 1)[0]?.trim() ?? null);
  if (!labelText) return null;
  return {
    label: labelText.slice(0, 80),
    citation: citationText,
    file: typeof file === 'string' && PDF_FILE_NAME.test(file) ? file : null,
    location:
      typeof location === 'string' && location.trim()
        ? location.trim().slice(0, 60)
        : null,
    page:
      typeof page === 'number' && Number.isInteger(page) && page >= 1
        ? page
        : null,
  };
}
